import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatErr, fmtMin, fmtLessonWhen } from "@/lib/api";
import { StatusBadge, FlagBadge } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { OutcomeBadge, OverallSummary, fmtWhen } from "./AssessmentsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, FileDown, Pencil, AlertTriangle, Save } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ParticipantDetail() {
  const { eid } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [assess, setAssess] = useState({ tests: [], summary: null });

  const load = useCallback(() => api.get(`/enrollments/${eid}/detail`).then((r) => { setD(r.data); setNote(r.data.enrollment.note_visible || ""); }), [eid]);
  useEffect(() => { load(); api.get(`/enrollments/${eid}/assessments`).then((r) => setAssess(r.data)); }, [load, eid]);

  if (!d) return <div className="text-slate-500">Caricamento…</div>;
  const { participant: p, course, summary: s } = d;

  const chartData = d.lessons.map((lr) => ({
    name: `L${lr.lesson.number}`,
    assenza: Math.round(lr.cumulative_absence_min / 60 * 100) / 100,
  }));
  const maxAbsHours = s.max_absence_hours;

  const download = (fmt) => {
    const token = localStorage.getItem("athena_token");
    fetch(`${BACKEND}/api/enrollments/${eid}/export?format=${fmt}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const url = URL.createObjectURL(b); const a = document.createElement("a"); a.href = url; a.download = `scheda_${p.last_name}.${fmt}`; a.click(); URL.revokeObjectURL(url); });
  };
  const saveNote = async () => { await api.put(`/enrollments/${eid}/note`, { message: note }); toast.success("Nota salvata (visibile al partecipante)"); };

  return (
    <div>
      <button onClick={() => navigate(`/corsi/${course.id}`)} className="text-sm text-slate-500 hover:text-slate-800 mb-2">← Registro del corso</button>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-extrabold text-slate-900">{p.first_name} {p.last_name}</h1>
          <p className="text-sm text-slate-500 mt-1">{p.email} · {course.title}</p>
          <div className="mt-2"><FlagBadge flag={d.flag} /></div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="export-card-pdf"><FileText size={15} className="mr-1" /> Scheda PDF</Button>
          <Button variant="outline" size="sm" onClick={() => download("xlsx")}><FileDown size={15} className="mr-1" /> Excel</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { l: "Ore frequentate", v: `${s.total_recognized_hm} su ${s.total_programmed_hm}`, c: "text-emerald-700" },
          { l: "Assenza (% monte ore)", v: `${s.absence_pct_progressive}%`, c: "text-slate-900" },
          { l: "Ore assenza accumulate", v: `${s.total_absence_hm} su ${s.max_absence_hm} max`, c: "text-rose-700" },
          { l: "Ore assenza residue", v: s.residual_absence_hm, c: s.over_limit ? "text-rose-700" : "text-slate-900" },
        ].map((k) => (
          <Card key={k.l} className="p-4 border-slate-200">
            <div className="text-xs text-slate-500 uppercase tracking-wider">{k.l}</div>
            <div className={`text-2xl font-display font-extrabold tabular mt-1 ${k.c}`}>{k.v}</div>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card className="p-5 border-slate-200 lg:col-span-2" data-testid="participant-absence-chart">
          <h3 className="font-display font-bold text-slate-900 mb-1">Andamento assenze cumulate</h3>
          <p className="text-xs text-slate-500 mb-4">Linea rossa = monte ore di assenza massimo consentito ({s.max_absence_hm} su {s.course_total_hm} totali).</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData} margin={{ left: -20 }}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#DC2626" stopOpacity={0.3} /><stop offset="95%" stopColor="#DC2626" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={11} stroke="#94a3b8" />
              <YAxis fontSize={11} stroke="#94a3b8" unit="h" />
              <Tooltip />
              <ReferenceLine y={maxAbsHours} stroke="#DC2626" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="assenza" stroke="#DC2626" fill="url(#g)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="font-display font-bold text-slate-900 mb-2">Nota visibile al partecipante</h3>
          <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. giustificazione, comunicazione…" data-testid="visible-note-input" />
          <Button size="sm" onClick={saveNote} className="mt-2 bg-[#0B192C] hover:bg-[#07111E]"><Save size={14} className="mr-1" /> Salva nota</Button>
          <div className="mt-4 pt-4 border-t">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Accessi al link personale</div>
            <div className="text-sm text-slate-600">{d.portal_accesses.length} consultazioni</div>
          </div>
        </Card>
      </div>

      {assess.tests.length > 0 && (
        <Card className="border-slate-200 overflow-hidden mb-6" data-testid="participant-assessments">
          <div className="px-4 py-3 bg-slate-50 text-sm font-semibold text-slate-700">Verifiche</div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Test</TableHead><TableHead>Svolto il</TableHead><TableHead>Punteggio</TableHead><TableHead>%</TableHead><TableHead>Soglia</TableHead><TableHead>Esito</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {assess.tests.map((a) => (
                <TableRow key={a.id} data-testid={`participant-assessment-${a.id}`}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtWhen(a.result)}</TableCell>
                  <TableCell className="tabular">{a.result && a.result.score !== null ? `${String(a.result.score).replace(".", ",")} / ${a.max_score}` : "—"}</TableCell>
                  <TableCell className="tabular">{a.result && a.result.pct !== null ? `${a.result.pct}%` : "—"}</TableCell>
                  <TableCell className="tabular text-slate-500">{a.pass_pct}%</TableCell>
                  <TableCell><OutcomeBadge r={a.result} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <OverallSummary s={assess.summary} />
        </Card>
      )}

      <Card className="border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 text-sm font-semibold text-slate-700">Dettaglio lezioni svolte</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Lez.</TableHead><TableHead>Data e orario</TableHead><TableHead>Durata</TableHead><TableHead>Presenza</TableHead>
            <TableHead>Assenza</TableHead><TableHead>% pres.</TableHead><TableHead>Accessi</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.lessons.map((lr) => (
              <LessonRow key={lr.lesson.id} eid={eid} lr={lr} reload={load} />
            ))}
            {d.lessons.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Nessuna lezione svolta.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function LessonRow({ eid, lr, reload }) {
  const a = lr.attendance;
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow data-testid={`detail-lesson-${lr.lesson.number}`}>
        <TableCell className="font-mono">{lr.lesson.number}</TableCell>
        <TableCell className="text-slate-500 whitespace-nowrap" data-testid={`detail-lesson-when-${lr.lesson.number}`}>{fmtLessonWhen(lr.lesson)}</TableCell>
        <TableCell>{a.programmed_minutes}′</TableCell>
        <TableCell className="text-emerald-700 tabular">{fmtMin(a.recognized_minutes)}</TableCell>
        <TableCell className="text-rose-700 tabular">{fmtMin(a.absence_minutes)}</TableCell>
        <TableCell className="tabular">{a.presence_pct}%</TableCell>
        <TableCell>{a.num_accessi ?? 0}</TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <StatusBadge status={a.status} />
            {a.anomalies?.length > 0 && <AlertTriangle size={14} className="text-orange-500" title={a.anomalies.join(", ")} />}
          </div>
        </TableCell>
        <TableCell><Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid={`expand-lesson-${lr.lesson.number}`}><Pencil size={14} /></Button></TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={9} className="bg-slate-50 p-4">
            <SessionEditor eid={eid} lessonId={lr.lesson.id} sessions={lr.sessions} attendance={a} reload={reload} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function SessionEditor({ eid, lessonId, sessions, attendance, reload }) {
  const adjust = async (payload) => {
    const reason = window.prompt("Motivazione della modifica (obbligatoria):");
    if (!reason) return;
    try { await api.post(`/enrollments/${eid}/lessons/${lessonId}/adjust`, { ...payload, reason }); toast.success("Modifica registrata (dato originale conservato)"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const [status, setStatus] = useState(attendance.status);

  return (
    <div>
      <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Accessi registrati ({sessions.length})</div>
      <div className="space-y-2 mb-4">
        {sessions.map((s) => (
          <div key={s.id} className="flex items-center gap-3 text-sm bg-white rounded-lg border border-slate-200 px-3 py-2 flex-wrap">
            <span className="font-mono text-xs text-slate-600">{s.join_raw} → {s.left_raw}</span>
            {s.moderator && <span className="text-[10px] bg-slate-200 rounded px-1.5">MOD</span>}
            {s.excluded && <span className="text-[10px] bg-rose-100 text-rose-700 rounded px-1.5">ESCLUSO</span>}
            <span className="text-xs text-slate-400">durata dich.: {s.duration_raw}</span>
            <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs"
              onClick={() => adjust({ field: "exclude", session_id: s.id, new_value: s.excluded ? "false" : "true" })}>
              {s.excluded ? "Reincludi" : "Escludi"}
            </Button>
          </div>
        ))}
        {sessions.length === 0 && <div className="text-sm text-slate-400">Nessun accesso (assente).</div>}
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs">Stato manuale</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[180px] h-8" data-testid="manual-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="presente">Presente</SelectItem>
              <SelectItem value="parziale">Presenza parziale</SelectItem>
              <SelectItem value="assente">Assente</SelectItem>
              <SelectItem value="giustificato">Giustificato</SelectItem>
              <SelectItem value="da_verificare">Da verificare</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={() => adjust({ field: "status", manual_status: status })} className="bg-[#0B192C] hover:bg-[#07111E]">Applica stato</Button>
        {attendance.over_duration && (
          <Button size="sm" variant="outline" onClick={() => adjust({ field: "allow_over_duration", new_value: "true" })}>
            Autorizza durata &gt; lezione
          </Button>
        )}
      </div>
    </div>
  );
}
